<template>
    <div class="quiz-block" :class="{'d-none': (step >= 0 && step !== index)}">
        <p class="quiz-title">{{ index + 1 }}. {{ question.title }}<span v-if="question.is_mandatory"> *</span></p>
        <div class="mb-4" v-if="question.file_type && question.file">
            <a v-if="question.file_type === 'image'" :href="question.file" target="_blank" rel="noreferrer noopener">
                <img :src="question.image_urls.large" v-on:error.once="setDefaultImage(question.file)"
                     alt="img">
            </a>
            <video v-else-if="question.file_type === 'video'" :src="question.file"
                   controls="controls" width="100%">
                <source id='mp4' :src="question.file" type='video/mp4'/>
                <source id='webm' :src="question.file" type='video/webm'/>
            </video>
            <custom-iframe
                v-else :url="question.file"
                width="100%"
                height="300px"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen></custom-iframe>
        </div>
        <div v-if="question.type === 'dropdown'" class="form-group">
            <select class="custom-select" aria-label="select" @change="e => chooseAnswer(e.target.value, question)">
                <option value="">{{ trans('select your answer') }}</option>
                <option v-for="(answer) in question.answers" :value="answer.id" :selected="getAnswer(question.id) && getAnswer(question.id).some(a => (a.original_id ? a.original_id : a.id) === answer.original_id)">{{ answer.title }}</option>
            </select>
        </div>

        <div v-else-if="question.type === 'textarea'" class="form-group">
            <textarea class="form-control" @input="e => writeAnswer(e.target.value, question)" :value="getAnswer(question.id)"></textarea>
        </div>

        <div v-else-if="question.type === 'text'" class="form-group">
            <input class="form-control" @input="e => writeAnswer(e.target.value, question)" :value="getAnswer(question.id)">
        </div>

        <div v-else-if="question.type === 'custom-file'" class="form-group">
            <file-selector
                accept-extensions=".jpg,.png,.mp4,.pdf,.csv,.xlsx,.ppt,.pptx,.xls,.mp3,.doc,.txt,.docx,.rtf,.xml,.zip,.json,.webp,.mov,.heif,.heic,.jpeg,.gif,.dng,.m4v,.3gp,.3g2,.webm,.m4a,.aac,.amr,.wav,.ogg,.opus"
                :multiple="true"
                :max-file-size="1024 * 1024 * 1024"
                @changed="(response) => handleFilesChanged(question, response)"
                @validated="handleFilesError"
            >
                {{ trans('Drop file here or') }} <span class="filepond--label-action">{{ trans('Browse') }}</span>
            </file-selector>

            <div v-for="(fileItem, index) in answerFiles(question.id)" class="file-item">
                <p>{{ fileItem.original_name }}</p>
                <span class="remove-button" @click="removeFile(index, question)">x</span>
            </div>
        </div>

        <div v-else>
            <one-answer
                v-for="(answer, index) in question.answers"
                :answer="answer"
                :question="question"
                :index="index"
                :key="index"
                :selected="getAnswer(question.id) && getAnswer(question.id).some(a => (a.original_id ? a.original_id : a.id) === answer.original_id)"
            />

            <other-answer
                v-if="question.has_other"
                :question="question"
                :selected="getAnswer(question.id) && getAnswer(question.id).some(a => a.type === 'other')"
                :title="(getAnswer(question.id) && getAnswer(question.id).some(a => a.type === 'other')) ? getAnswer(question.id).find(a => a.type === 'other').title : ''"
            />
        </div>
    </div>
</template>

<script>
import CustomIframe from "../../../../../TenantDashboardQuizzes/Resources/assets/js/components/CustomIframe";
import OneAnswer from "./OneAnswer";
import OtherAnswer from "./OtherAnswer";
import axios from "axios";
import {heicToJpeg} from "../helpers/heicToJpeg";

export default {
    name: "OneQuestion",
    props: {
        question: {
            required: true,
            type: Object
        },
        index: {
            required: true,
            type: Number
        },
        step: {
            required: false,
            type: Number,
        }
    },
    components: {
        'custom-iframe': CustomIframe,
        'one-answer': OneAnswer,
        'other-answer': OtherAnswer
    },
    methods: {
        setDefaultImage(image) {
            const imgElement = event.target;
            imgElement.src = image;
        },

        handleFilesError(error) {
            if (error && error !== true) {
                toastr.error(trans(error, 'quiz'))
            }
        },
        getAnswer(questionId) {
          return this.$store.state.userAnswers[questionId]?.answers;
        },

        async handleFilesChanged(question, response) {
            $('.loader').addClass('show');

            const files = await Promise.all(Array.from(response).map(heicToJpeg));

            const promises = [];
            for (let i = 0; i < files.length; i++) {
                const formData = new FormData();
                formData.append("file", files[i]);
                formData.append("type", 'custom');
                formData.append("mimes", 'jpg,jpeg,png,mp4,pdf,csv,xlsx,ppt,pptx,xls,mp3,doc,txt,docx,rtf,xml,zip,json,webp,mov,heif,heic,heics,heifs,gif,dng,tif,m4v,3gp,3g2,3gpp,webm,weba,m4a,aac,adts,amr,wav,ogg');
                const config = {headers: {'Content-Type': 'multipart/form-data'}};
                const originalName = files[i].name;

                const res = axios.post('/dashboard/quizzes/upload', formData, config).then(res => {
                    const file = res.data;
                    file.original_name = originalName;
                    this.writeAnswerFile(file, question);
                }).catch(e => {
                    const errors = e.response.data.errors;
                    for (let field in errors) {
                        toastr.error(errors[field][0])
                    }
                });

                promises.push(res);
            }

            await Promise.all(promises);

            $('.loader').removeClass('show');
        },

        answerFiles(question_id) {
            if (!this.$store.state?.userAnswers[question_id] || !this.$store.state?.userAnswers[question_id]?.answers) {
                return [];
            }

            return this.$store.state.userAnswers[question_id].answers;
        }
    }
}
</script>


<style scoped>
.quiz-block img {
    max-width: 100%;
    max-height: 300px;
}
</style>
